/* __next_internal_client_entry_do_not_use__ default auto */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useRef } from 'react';
import { useComponentProps } from '@sitecore-content-sdk/nextjs';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { SvgIcon } from 'helpers/SvgIcon';
import { useNavigationState } from 'lib/hooks/useNavigationState';
import { isValidNavigationItem, getValidChildren, createLinkField } from 'lib/utils/navigation-utils';
import { ContextualNavItem } from './ContextualNavItem';
import { getTestProps } from 'lib/testing/utils';
import { contextualNavTailwindVariants } from './ContextualNav';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
import useIsEditing from 'lib/hooks/useIsEditing';
// NavList component for rendering navigation items
const NavList = ({ items, currentPageId, expandedItems, onToggle, level = 1, isNavExpanded = true, isParentExpanded = true })=>{
    const { list } = contextualNavTailwindVariants();
    return /*#__PURE__*/ _jsx("ul", {
        className: level === 1 ? list() : undefined,
        ...getTestProps(`component-contextual-nav-${level}`),
        children: items.map((item, index)=>{
            const isCurrent = item.id === currentPageId;
            const isManuallyExpanded = expandedItems.has(item.id);
            const shouldShowChildren = isCurrent ? !expandedItems.has(`collapsed-${item.id}`) : isManuallyExpanded;
            const validChildren = getValidChildren(item);
            const showLevelToggle = validChildren.length > 0;
            const { sublist, sublistChild, listItem } = contextualNavTailwindVariants({
                isExpanded: shouldShowChildren,
                current: isCurrent
            });
            return /*#__PURE__*/ _jsxs("li", {
                className: level === 1 ? listItem() : undefined,
                ...getTestProps(`contextual-nav-item-${level}-${index}`),
                children: [
                    /*#__PURE__*/ _jsx(ContextualNavItem, {
                        item: item,
                        isCurrent: isCurrent,
                        isExpanded: shouldShowChildren,
                        hasChildren: validChildren.length > 0,
                        onToggle: onToggle,
                        level: level,
                        showToggle: showLevelToggle,
                        isNavExpanded: isNavExpanded,
                        isParentExpanded: isParentExpanded
                    }),
                    validChildren.length > 0 && shouldShowChildren && /*#__PURE__*/ _jsx("div", {
                        className: level === 1 ? sublist() : sublistChild(),
                        role: "region",
                        "aria-labelledby": `contextual-nav-link-${item.id}`,
                        id: `contextual-nav-sublist-${item.id}`,
                        children: /*#__PURE__*/ _jsx(NavList, {
                            items: validChildren,
                            currentPageId: currentPageId,
                            expandedItems: expandedItems,
                            onToggle: onToggle,
                            level: level + 1,
                            isNavExpanded: isNavExpanded,
                            isParentExpanded: shouldShowChildren
                        })
                    })
                ]
            }, item.id);
        })
    });
};
// Base ContextualNav component that accepts initial state
const ContextualNavBase = ({ props, initialNavExpanded = true, showToggleButton = true })=>{
    const isEditing = useIsEditing();
    const ctxProps = useComponentProps(props?.rendering?.uid);
    const staticProps = props?.staticProps ?? ctxProps?.staticProps;
    const mainToggleRef = useRef(null);
    const navigationItems = (staticProps?.navRoot?.children?.results || []).filter(isValidNavigationItem);
    const currentPageId = staticProps?.currentPage?.id || '';
    const ancestors = staticProps?.currentPage?.ancestors || [];
    const { expandedItems, isNavExpanded, handleItemToggle, handleNavToggle } = useNavigationState(currentPageId, ancestors, navigationItems, initialNavExpanded);
    if (!staticProps?.navRoot) {
        return /*#__PURE__*/ _jsx(_Fragment, {});
    }
    if (!isValidNavigationItem(staticProps.navRoot)) {
        return /*#__PURE__*/ _jsx(_Fragment, {});
    }
    const isMainNavActive = staticProps.navRoot.url?.path === staticProps.currentPage.url?.path || staticProps.navRoot.id === staticProps.currentPage.id;
    const { container, heading, link, mainButton, mainLink, navContainer, mainNavItem, mainIcon } = contextualNavTailwindVariants({
        isExpanded: isNavExpanded,
        current: isMainNavActive
    });
    return /*#__PURE__*/ _jsx("aside", {
        className: container(),
        "data-component": "authorable/shared/lists/contextual-nav",
        ...editingHydrationProps(isEditing),
        id: props.rendering.uid,
        ...getTestProps(`contextual-nav-${initialNavExpanded ? 'desktop' : 'mobile'}-${props?.rendering?.uid}`),
        children: /*#__PURE__*/ _jsxs("nav", {
            role: "navigation",
            "aria-label": "Contextual Navigation",
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: heading(),
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: mainNavItem(),
                        children: [
                            /*#__PURE__*/ _jsx(LinkWrapper, {
                                field: createLinkField(staticProps.navRoot),
                                className: mainLink() + link(),
                                ctaComponentClass: "default",
                                ...getTestProps(`contextual-nav-main-nav`)
                            }),
                            showToggleButton ? /*#__PURE__*/ _jsx("button", {
                                className: mainButton(),
                                "aria-expanded": isNavExpanded,
                                "aria-controls": "contextual-nav-list",
                                "aria-label": isNavExpanded ? 'Collapse navigation' : 'Expand navigation',
                                onClick: handleNavToggle,
                                type: "button",
                                ref: mainToggleRef,
                                ...getTestProps(`contextual-nav-toggle-btn`),
                                children: /*#__PURE__*/ _jsx(SvgIcon, {
                                    className: mainIcon(),
                                    icon: "chevron-down",
                                    size: "xs"
                                })
                            }) : /*#__PURE__*/ _jsx(SvgIcon, {
                                className: mainIcon(),
                                icon: "arrow-dash-right",
                                size: "s"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: navContainer(),
                    id: "contextual-nav-list",
                    "aria-hidden": !isNavExpanded,
                    children: navigationItems.length > 0 ? /*#__PURE__*/ _jsx(NavList, {
                        items: navigationItems,
                        currentPageId: currentPageId,
                        expandedItems: expandedItems,
                        onToggle: handleItemToggle,
                        level: 1,
                        isNavExpanded: isNavExpanded,
                        isParentExpanded: true
                    }) : /*#__PURE__*/ _jsx("p", {
                        children: "No navigation items found"
                    })
                })
            ]
        })
    });
};
const ContextualNavMobile = (props)=>/*#__PURE__*/ _jsx(ContextualNavBase, {
        props: props,
        initialNavExpanded: false,
        showToggleButton: true
    });
const ContextualNavDesktop = (props)=>/*#__PURE__*/ _jsx(ContextualNavBase, {
        props: props,
        initialNavExpanded: true,
        showToggleButton: false
    });
const ContextualNavClient = (props)=>/*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "hidden md:block",
                children: /*#__PURE__*/ _jsx(ContextualNavDesktop, {
                    ...props
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "block md:hidden",
                children: /*#__PURE__*/ _jsx(ContextualNavMobile, {
                    ...props
                })
            })
        ]
    });
export default ContextualNavClient;
